/** Resolve comment authorIds → display labels via Cloud (email until #113). */

import { authorizedFetch, getCloudSession } from "../lib/cloudAuth";

const cache = new Map<string, string>();
let inflight: Promise<void> | null = null;
let pendingIds = new Set<string>();

/** Test helper: clear the in-memory label cache. */
export function resetCommentAuthorLabelCache(): void {
  cache.clear();
  pendingIds = new Set();
  inflight = null;
}

export function peekCommentAuthorLabel(userId: string): string | undefined {
  return cache.get(userId);
}

/**
 * Ensure labels for the given user ids are loaded. Returns the subset of the
 * cache covering `ids` (may be partial if the request fails or the user is
 * signed out).
 */
export async function ensureCommentAuthorLabels(
  ids: string[],
): Promise<Record<string, string>> {
  const needed = [
    ...new Set(ids.map((id) => id.trim()).filter((id) => id.length > 0)),
  ].filter((id) => !cache.has(id));

  if (needed.length > 0 && getCloudSession() !== null) {
    for (const id of needed) pendingIds.add(id);
    if (inflight === null) {
      inflight = flushPending().finally(() => {
        inflight = null;
      });
    }
    await inflight;
  }

  const out: Record<string, string> = {};
  for (const id of ids) {
    const trimmed = id?.trim();
    if (trimmed.length === 0) continue;
    const label = cache.get(trimmed);
    if (typeof label === "string" && label.length > 0) out[trimmed] = label;
  }
  return out;
}

async function flushPending(): Promise<void> {
  const ids = [...pendingIds];
  pendingIds = new Set();
  if (ids.length === 0) return;

  try {
    const res = await authorizedFetch("/v1/users/labels", {
      method: "POST",
      body: JSON.stringify({ ids }),
    });
    if (!res.ok) return;
    const body = (await res.json()) as {
      users?: Array<{ id?: string; label?: string }>;
    };
    for (const user of body.users ?? []) {
      const id = user.id?.trim();
      const label = user.label?.trim();
      if (
        typeof id === "string" &&
        id.length > 0 &&
        typeof label === "string" &&
        label.length > 0
      )
        cache.set(id, label);
    }
  } catch {
    // Offline / unsigned: leave unresolved; legacy authorName still works.
  }
}
